import React from "react";
import { API } from "@/lib/api";

// Password-protected standalone page.
// Password is validated against POST /api/admin/poster/verify (ADMIN_PASSWORD env var).
// The entered password is kept in sessionStorage and sent via `X-Admin-Password` header.
// This page is intentionally NOT wrapped in the main AdminLayout (no JWT required).

const STORAGE_KEY = "batik_poster_admin_pwd";

const STATUS_LABEL = {
  draft: "Draft",
  published: "Tayang",
  archived: "Arsip",
};

const STATUS_PILL = {
  draft: "bg-amber-100 text-amber-800 border-amber-200",
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
  archived: "bg-slate-200 text-slate-700 border-slate-300",
};

function LoginGate({ onSuccess }) {
  const [pwd, setPwd] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await fetch(`${API}/admin/poster/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pwd }),
      });
      if (!r.ok) {
        setErr("Password salah.");
        return;
      }
      sessionStorage.setItem(STORAGE_KEY, pwd);
      onSuccess(pwd);
    } catch (_e) {
      setErr("Gagal menghubungi server.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-card border border-border rounded-lg p-8 shadow-xl"
        data-testid="poster-admin-login-form"
      >
        <h1 className="font-serif text-2xl text-primary mb-1">Admin Poster</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Masukkan password admin untuk mengelola poster promo.
        </p>
        <label className="text-sm font-medium block mb-1">Password</label>
        <input
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          className="w-full border border-input rounded-md px-3 py-2 bg-background mb-4 focus:outline-none focus:ring-2 focus:ring-accent"
          autoFocus
          required
          data-testid="poster-admin-password-input"
        />
        {err && (
          <p className="text-sm text-red-600 mb-3" data-testid="poster-admin-login-error">
            {err}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="btn-primary w-full"
          data-testid="poster-admin-login-submit"
        >
          {busy ? "Memeriksa…" : "Masuk"}
        </button>
      </form>
    </div>
  );
}

function UploadCard({ pwd, onUploaded }) {
  // Uploads use POSTER_API_KEY, which the admin must know separately.
  // We allow entering it here and remember it in sessionStorage too.
  const KEY_STORAGE = "batik_poster_api_key";
  const [apiKey, setApiKey] = React.useState(
    () => sessionStorage.getItem(KEY_STORAGE) || ""
  );
  const [file, setFile] = React.useState(null);
  const [judul, setJudul] = React.useState("");
  const [deskripsi, setDeskripsi] = React.useState("");
  const [altText, setAltText] = React.useState("");
  const [paketKode, setPaketKode] = React.useState("");
  const [tglBerakhir, setTglBerakhir] = React.useState("");
  const [urutan, setUrutan] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState("");

  async function submit(e) {
    e.preventDefault();
    if (!file) { setMsg("Pilih file gambar dulu."); return; }
    setBusy(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("judul", judul);
      fd.append("deskripsi", deskripsi);
      fd.append("alt_text", altText);
      if (paketKode) fd.append("paket_kode", paketKode);
      if (tglBerakhir) fd.append("tgl_berakhir", tglBerakhir);
      fd.append("urutan", String(urutan || 0));

      const r = await fetch(`${API}/posters`, {
        method: "POST",
        headers: { "X-API-Key": apiKey },
        body: fd,
      });
      if (!r.ok) {
        const t = await r.text();
        setMsg(`Gagal upload: ${r.status} ${t}`);
        return;
      }
      sessionStorage.setItem(KEY_STORAGE, apiKey);
      setFile(null); setJudul(""); setDeskripsi(""); setAltText("");
      setPaketKode(""); setTglBerakhir(""); setUrutan(0);
      setMsg("Berhasil diupload sebagai draft.");
      onUploaded();
    } catch (_e) {
      setMsg("Gagal menghubungi server.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="bg-card border border-border rounded-lg p-6 mb-8 shadow-sm"
      data-testid="poster-upload-form"
    >
      <h2 className="font-serif text-xl text-primary mb-4">Upload Poster Baru (status: draft)</h2>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="text-sm font-medium block mb-1">X-API-Key (POSTER_API_KEY)</label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Diperlukan untuk upload"
            className="w-full border border-input rounded-md px-3 py-2 bg-background"
            required
            data-testid="poster-upload-api-key"
          />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Judul</label>
          <input value={judul} onChange={(e)=>setJudul(e.target.value)} required
            className="w-full border border-input rounded-md px-3 py-2 bg-background"
            data-testid="poster-upload-judul" />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Alt text (SEO/aksesibilitas)</label>
          <input value={altText} onChange={(e)=>setAltText(e.target.value)}
            className="w-full border border-input rounded-md px-3 py-2 bg-background"
            data-testid="poster-upload-alt" />
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium block mb-1">Deskripsi</label>
          <textarea value={deskripsi} onChange={(e)=>setDeskripsi(e.target.value)} rows={3}
            className="w-full border border-input rounded-md px-3 py-2 bg-background"
            data-testid="poster-upload-deskripsi" />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Paket kode (opsional)</label>
          <input value={paketKode} onChange={(e)=>setPaketKode(e.target.value)}
            className="w-full border border-input rounded-md px-3 py-2 bg-background"
            data-testid="poster-upload-paket" />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Tgl berakhir (opsional)</label>
          <input type="date" value={tglBerakhir} onChange={(e)=>setTglBerakhir(e.target.value)}
            className="w-full border border-input rounded-md px-3 py-2 bg-background"
            data-testid="poster-upload-tgl" />
        </div>
        <div>
          <label className="text-sm font-medium block mb-1">Urutan</label>
          <input type="number" value={urutan} onChange={(e)=>setUrutan(parseInt(e.target.value||"0", 10))}
            className="w-full border border-input rounded-md px-3 py-2 bg-background"
            data-testid="poster-upload-urutan" />
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium block mb-1">File (jpg/png/webp, maks 5 MB)</label>
          <input type="file" accept="image/jpeg,image/png,image/webp"
            onChange={(e)=>setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm"
            required
            data-testid="poster-upload-file" />
        </div>
      </div>
      {msg && (
        <p className="mt-4 text-sm" data-testid="poster-upload-msg">
          {msg}
        </p>
      )}
      <div className="mt-5 flex justify-end">
        <button type="submit" disabled={busy} className="btn-primary" data-testid="poster-upload-submit">
          {busy ? "Mengirim…" : "Upload Poster"}
        </button>
      </div>
    </form>
  );
}

function PosterRow({ p, pwd, onChanged }) {
  const [draft, setDraft] = React.useState({
    judul: p.judul,
    deskripsi: p.deskripsi,
    alt_text: p.alt_text,
    tgl_berakhir: p.tgl_berakhir || "",
    urutan: p.urutan,
  });
  const [busy, setBusy] = React.useState(false);

  async function patch(body) {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/poster/${p.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Password": pwd,
        },
        body: JSON.stringify(body),
      });
      if (r.ok) onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className="border-t border-border align-top" data-testid={`poster-row-${p.id}`}>
      <td className="p-3 w-28">
        <img
          src={`${API}/posters/${p.id}/gambar`}
          alt={p.alt_text || p.judul}
          className="w-24 aspect-[3/4] object-cover rounded border border-border"
        />
      </td>
      <td className="p-3 min-w-[220px]">
        <input value={draft.judul} onChange={(e)=>setDraft({...draft, judul:e.target.value})}
          className="w-full border border-input rounded-md px-2 py-1 bg-background text-sm mb-2"
          data-testid={`poster-row-judul-${p.id}`} />
        <textarea value={draft.deskripsi} onChange={(e)=>setDraft({...draft, deskripsi:e.target.value})} rows={2}
          className="w-full border border-input rounded-md px-2 py-1 bg-background text-sm mb-2"
          data-testid={`poster-row-deskripsi-${p.id}`} />
        <input value={draft.alt_text} onChange={(e)=>setDraft({...draft, alt_text:e.target.value})}
          placeholder="alt text"
          className="w-full border border-input rounded-md px-2 py-1 bg-background text-xs" />
      </td>
      <td className="p-3 w-40">
        <input type="date" value={draft.tgl_berakhir || ""}
          onChange={(e)=>setDraft({...draft, tgl_berakhir:e.target.value})}
          className="w-full border border-input rounded-md px-2 py-1 bg-background text-sm"
          data-testid={`poster-row-tgl-${p.id}`} />
      </td>
      <td className="p-3 w-24">
        <input type="number" value={draft.urutan}
          onChange={(e)=>setDraft({...draft, urutan:parseInt(e.target.value||"0", 10)})}
          className="w-full border border-input rounded-md px-2 py-1 bg-background text-sm"
          data-testid={`poster-row-urutan-${p.id}`} />
      </td>
      <td className="p-3 w-28">
        <span className={`inline-block px-2 py-1 text-xs rounded border ${STATUS_PILL[p.status] || ""}`}>
          {STATUS_LABEL[p.status] || p.status}
        </span>
        <div className="mt-2 flex flex-col gap-1 text-xs">
          <label className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={!!p.tampil_popup}
              onChange={(e) => patch({ tampil_popup: e.target.checked })}
              data-testid={`poster-row-popup-${p.id}`}
            />
            Pop-up
          </label>
          <label className="inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={!!p.tampil_rolling}
              onChange={(e) => patch({ tampil_rolling: e.target.checked })}
              data-testid={`poster-row-rolling-${p.id}`}
            />
            Rolling
          </label>
        </div>
      </td>
      <td className="p-3 w-64">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => patch({
              judul: draft.judul,
              deskripsi: draft.deskripsi,
              alt_text: draft.alt_text,
              tgl_berakhir: draft.tgl_berakhir || null,
              urutan: draft.urutan,
            })}
            className="text-xs px-3 py-1 rounded-md border border-input hover:bg-muted"
            data-testid={`poster-row-save-${p.id}`}
          >
            Simpan
          </button>
          {p.status !== "published" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => patch({ status: "published" })}
              className="text-xs px-3 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid={`poster-row-publish-${p.id}`}
            >
              Tayangkan
            </button>
          )}
          {p.status !== "archived" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => patch({ status: "archived" })}
              className="text-xs px-3 py-1 rounded-md bg-slate-600 text-white hover:bg-slate-700"
              data-testid={`poster-row-archive-${p.id}`}
            >
              Arsipkan
            </button>
          )}
          {p.status !== "draft" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => patch({ status: "draft" })}
              className="text-xs px-3 py-1 rounded-md border border-input hover:bg-muted"
              data-testid={`poster-row-draft-${p.id}`}
            >
              Jadikan Draft
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function Dashboard({ pwd, onLogout }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/poster/list`, {
        headers: { "X-Admin-Password": pwd },
      });
      if (r.ok) {
        const data = await r.json();
        setRows(Array.isArray(data) ? data : []);
      } else if (r.status === 401) {
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-muted/20">
      <header className="bg-primary text-primary-foreground py-5">
        <div className="container-x flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl">Admin Poster Promo</h1>
            <p className="text-sm opacity-80">BATIK Tour &amp; Travel</p>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="text-sm px-4 py-2 rounded-md bg-white/10 hover:bg-white/20"
            data-testid="poster-admin-logout"
          >
            Keluar
          </button>
        </div>
      </header>
      <main className="container-x py-10">
        <UploadCard pwd={pwd} onUploaded={load} />

        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-serif text-xl text-primary">Daftar Poster</h2>
            <button
              type="button"
              onClick={load}
              className="text-xs px-3 py-1 rounded-md border border-input hover:bg-muted"
              data-testid="poster-admin-refresh"
            >
              Muat Ulang
            </button>
          </div>
          {loading ? (
            <p className="p-6 text-sm text-muted-foreground">Memuat…</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">Belum ada poster. Upload di atas.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="poster-admin-table">
                <thead className="bg-muted/50 text-xs uppercase">
                  <tr>
                    <th className="p-3 text-left">Gambar</th>
                    <th className="p-3 text-left">Judul / Deskripsi / Alt</th>
                    <th className="p-3 text-left">Tgl Berakhir</th>
                    <th className="p-3 text-left">Urutan</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <PosterRow key={p.id} p={p} pwd={pwd} onChanged={load} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function PosterAdmin() {
  const [pwd, setPwd] = React.useState(() => sessionStorage.getItem(STORAGE_KEY) || "");

  function logout() {
    sessionStorage.removeItem(STORAGE_KEY);
    setPwd("");
  }

  if (!pwd) return <LoginGate onSuccess={setPwd} />;
  return <Dashboard pwd={pwd} onLogout={logout} />;
}
